import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getFunctions, Functions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';
import { 
  initializeAppCheck, 
  AppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Determine if we're in development mode (Vite sets this)
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development';
// Only allow emulators in development mode
const useEmulators = isDevelopment && import.meta.env.VITE_USE_EMULATORS === 'true';
const FUNCTIONS_REGION = 'us-central1';
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

let app: FirebaseApp;
let functions: Functions;
let analytics: Analytics | null = null;
let appCheck: AppCheck | null = null;

if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

functions = getFunctions(app, FUNCTIONS_REGION);

// Connect to emulators if enabled
if (useEmulators) {
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  console.log('🔧 Using Firebase emulators');
}

// Initialize App Check for bot protection
// In development with emulators, use debug token
// In production, use reCAPTCHA Enterprise
if (recaptchaSiteKey) {
  if (useEmulators && isDevelopment) {
    // SECURITY: Only enable debug mode in development
    // This double-check prevents accidental production debug token exposure
    // Enable debug mode BEFORE initializing App Check
    // The debug token printed in console must be registered in Firebase Console:
    // Firebase Console → App Check → Apps → Manage debug tokens
    // @ts-expect-error - debug token for local development
    self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    console.log('🔧 App Check debug mode enabled (add debug token to Firebase Console)');
  } else if (useEmulators && !isDevelopment) {
    // SECURITY: If somehow emulators are enabled in production, log a warning and don't enable debug token
    console.error('⚠️ SECURITY WARNING: Emulator flag set but not in development mode. Debug token NOT enabled.');
  }
  
  // Use reCAPTCHA Enterprise for both dev and prod
  appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
  
  if (!useEmulators) {
    console.log('🛡️ App Check with reCAPTCHA Enterprise enabled');
  }
} else {
  console.warn('⚠️ App Check not configured - VITE_RECAPTCHA_SITE_KEY not set');
}

// Initialize analytics (only in production and if supported)
if (!useEmulators && firebaseConfig.measurementId) {
  isSupported().then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}

/**
 * Get the base URL for HTTP functions (non-callable)
 * Use this for functions defined with onRequest()
 */
export const getFunctionUrl = (functionName: string): string => {
  if (useEmulators) {
    return `http://127.0.0.1:5001/${firebaseConfig.projectId}/${FUNCTIONS_REGION}/${functionName}`;
  }
  return `https://${FUNCTIONS_REGION}-${firebaseConfig.projectId}.cloudfunctions.net/${functionName}`;
};

export { app, functions, analytics, appCheck };
